<template>
  <div class="quantity-chart-container mt-5">
    <div v-if="isPdfContent">
      <span class="pdf-chart-title">{{ tabItems[tab].name }}</span>
      <div>
        <div v-if="metrics && metrics.length" class="apex-chart-section">
          <apexchart width="100%" type="bar" :options="options" :series="pdfSeries" height="380px" />
        </div>
        <chart-empty-state v-else />
      </div>
    </div>
    <mf-card-container
      v-else
      v-model="search"
      title="Quantidade por loja"
      description="Análise das unidades vendidas na oferta em cada loja."
      placeholder="Buscar loja"
      useSearch
    >
      <template v-slot:filters>
        <export-data :data-fetcher="getDataToExport" filename="quantidade_por_loja" ghost />
      </template>
      <div class="chart-container">
        <mf-tabs-menu :key="`tabs-of-${offerType}`" v-model="tab" :tabs="tabItems" class="my-4" />
        <div v-if="metrics && metrics.length" class="apex-chart-section">
          <div :style="{ width: apexChartWidth, minWidth: '100%' }">
            <apexchart width="100%" type="bar" :options="options" :series="series" height="292px" />
          </div>
        </div>
        <chart-empty-state v-else />
      </div>
    </mf-card-container>
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import ExportData from '../export-data/ExportData.vue'
import ChartEmptyState from './ChartEmptyState.vue'
import { localeString } from '@/config/helpers.js'
export default {
  name: 'QuantityPerUnitChart',
  components: {
    ExportData,
    ChartEmptyState,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    isPdfContent: {
      type: Boolean,
      default: false,
    },
    tabValue: {
      type: Number,
      default: 0,
    },
    offerType: {
      type: String,
      validator: (value) =>
        [
          'general',
          'percentage',
          'dynamic_pricing_percentage',
          'takepay',
          'combo',
          'cashback_percentage',
          'cashback_absolute',
          'points',
          'nextbuycashback',
          'dynamic_rule',
          'crosssell',
        ].includes(value),
    },
    metrics: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    search: '',
    tab: 0,
    pdfSeries: [],
    benefitTypes: ['cashback_percentage', 'cashback_absolute', 'points'],
  }),
  computed: {
    tabItems() {
      const items = []
      if (this.offerType === 'combo') {
        items.push(
          {
            name: 'Produtos gatilho ',
            itemsLength: 0,
            haveLength: false,
          },
          {
            name: 'Produtos alvo ',
            itemsLength: 0,
            haveLength: false,
          }
        )
      } else {
        items.push({
          name: 'Produtos vendidos ',
          itemsLength: 0,
          haveLength: false,
        })
      }
      items.push({
        name: `Clientes e ${this.benefitTypes.includes(this.offerType) ? 'benefícios' : 'descontos'}`,
        itemsLength: 0,
        haveLength: false,
      })
      return items
    },
    comboQuantityProductsMetrics() {
      return this.tab
        ? this.searchedMetrics
            .filter((unit) => this.computedUnits.includes(unit.unit_name))
            .map((unit) => unit.metrics[this.offerType].products_metrics.totals.total.quantity)
        : this.searchedMetrics
            .filter((unit) => this.computedUnits.includes(unit.unit_name))
            .map((unit) => unit.metrics[this.offerType].products_trigger_metrics.totals.total.quantity)
    },
    comboClubQuantityProductsMetrics() {
      return this.tab
        ? this.searchedMetrics
            .filter((unit) => this.computedUnits.includes(unit.unit_name))
            .map((unit) => unit.metrics[this.offerType].products_metrics.totals.total.loyalty_quantity)
        : this.searchedMetrics
            .filter((unit) => this.computedUnits.includes(unit.unit_name))
            .map((unit) => unit.metrics[this.offerType].products_trigger_metrics.totals.total.loyalty_quantity)
    },
    comboQuantityOnOfferMetrics() {
      return this.tab
        ? this.searchedMetrics
            .filter((unit) => this.computedUnits.includes(unit.unit_name))
            .map((unit) => unit.metrics[this.offerType].products_metrics.totals.sales.quantity)
        : this.searchedMetrics
            .filter((unit) => this.computedUnits.includes(unit.unit_name))
            .map((unit) => unit.metrics[this.offerType].products_trigger_metrics.totals.sales.quantity)
    },
    quantityTotalMetrics() {
      return this.searchedMetrics
        .filter((unit) => this.computedUnits.includes(unit.unit_name))
        .map((unit) => unit.metrics[this.offerType].products_metrics.totals.total.quantity)
    },
    eligibleMetrics() {
      return this.searchedMetrics
        .filter((unit) => this.computedUnits.includes(unit.unit_name))
        .map((unit) => unit.metrics[this.offerType].eligible_metrics.loyalty_quantity)
    },
    quantitySalesQuantityMetrics() {
      return this.searchedMetrics
        .filter((unit) => this.computedUnits.includes(unit.unit_name))
        .map((unit) => unit.metrics[this.offerType].products_metrics.totals.sales.quantity)
    },
    loyaltyMetrics() {
      return this.searchedMetrics
        .filter((unit) => this.computedUnits.includes(unit.unit_name))
        .map((unit) => unit.metrics[this.offerType].products_metrics.totals.total.loyalty_quantity)
    },
    quantitySalesDiscountMetrics() {
      return this.searchedMetrics
        .filter((unit) => this.computedUnits.includes(unit.unit_name))
        .map((unit) => unit.metrics[this.offerType].products_metrics.totals.sales.quantity)
    },
    clientsMetrics() {
      return this.searchedMetrics
        .filter((unit) => this.computedUnits.includes(unit.unit_name))
        .map((unit) => unit.metrics[this.offerType].products_metrics.totals.sales.clients)
    },
    discountsMetrics() {
      return this.searchedMetrics
        .filter((unit) => this.computedUnits.includes(unit.unit_name))
        .map((unit) => {
          if (this.benefitTypes.includes(this.offerType)) {
            return unit.metrics[this.offerType].products_metrics.totals.sales[this.offerType === 'points' ? 'points' : 'cashback']
          } else {
            return unit.metrics[this.offerType].products_metrics.totals.sales.discounts
          }
        })
    },
    sortMetrics() {
      if (this.metrics) {
        const referenceValues = cloneDeep(this.metrics)
        if (this.offerType === 'combo' && this.tab !== 2) {
          return this.tab
            ? referenceValues.sort((a, b) => {
                return b.metrics[this.offerType].products_metrics.totals.total.quantity - a.metrics[this.offerType].products_metrics.totals.total.quantity
              })
            : referenceValues.sort((a, b) => {
                return (
                  b.metrics[this.offerType].products_trigger_metrics.totals.total.quantity -
                  a.metrics[this.offerType].products_trigger_metrics.totals.total.quantity
                )
              })
        } else if (this.offerType === 'takepay' || !this.tab) {
          return referenceValues.sort((a, b) => {
            return b.metrics[this.offerType].products_metrics.totals.total.quantity - a.metrics[this.offerType].products_metrics.totals.total.quantity
          })
        } else {
          return referenceValues.sort((a, b) => {
            return b.metrics[this.offerType].products_metrics.totals.sales.clients - a.metrics[this.offerType].products_metrics.totals.sales.clients
          })
        }
      } else return []
    },
    searchedMetrics() {
      return this.sortMetrics.filter((item) => item.unit_name.toLowerCase().includes(this.search.toLowerCase()))
    },
    series() {
      if (this.offerType === 'combo' && this.tab !== 2) {
        return [
          {
            name: `Unidades vendidas produto ${this.tab ? 'alvo' : 'gatilho'} (Clube e não clube)`,
            data: this.isPdfContent ? this.comboQuantityProductsMetrics.slice(0, 10) : this.comboQuantityProductsMetrics,
          },
          {
            name: `Unidades vendidas produto ${this.tab ? 'alvo' : 'gatilho'} (Somente clube)`,
            data: this.isPdfContent ? this.comboClubQuantityProductsMetrics.slice(0, 10) : this.comboClubQuantityProductsMetrics,
          },
          {
            name: 'Unidades vendidas participantes da oferta',
            data: this.isPdfContent ? this.comboQuantityOnOfferMetrics.slice(0, 10) : this.comboQuantityOnOfferMetrics,
          },
        ]
      } else if (this.offerType === 'takepay' && !this.tab) {
        return [
          {
            name: 'Unidades vendidas (Clube e não clube)',
            data: this.isPdfContent ? this.quantityTotalMetrics.slice(0, 10) : this.quantityTotalMetrics,
          },
          {
            name: 'Unidades elegíveis',
            data: this.isPdfContent ? this.eligibleMetrics.slice(0, 10) : this.eligibleMetrics,
          },
          {
            name: 'Unidades gratuitas',
            data: this.isPdfContent ? this.quantitySalesQuantityMetrics.slice(0, 10) : this.quantitySalesQuantityMetrics,
          },
        ]
      } else if (!this.tab) {
        return [
          {
            name: 'Unidades vendidas (Clube e não clube)',
            data: this.isPdfContent ? this.quantityTotalMetrics.slice(0, 10) : this.quantityTotalMetrics,
          },
          {
            name: 'Unidades vendidas (somente clube)',
            data: this.isPdfContent ? this.loyaltyMetrics.slice(0, 10) : this.loyaltyMetrics,
          },
          {
            name: 'Unidades com desconto',
            data: this.isPdfContent ? this.quantitySalesDiscountMetrics.slice(0, 10) : this.quantitySalesDiscountMetrics,
          },
        ]
      } else {
        return [
          {
            name: 'Clientes',
            data: this.isPdfContent ? this.clientsMetrics.slice(0, 10) : this.clientsMetrics,
          },
          {
            name: this.benefitTypes.includes(this.offerType) ? 'Benefícios' : 'Descontos',
            data: this.isPdfContent ? this.discountsMetrics.slice(0, 10) : this.discountsMetrics,
          },
        ]
      }
    },
    computedUnits() {
      return this.searchedMetrics.map((unit) => unit.unit_name)
    },
    formattedUnits() {
      const units = this.computedUnits.map((unit) => {
        const splittedName = unit.split(' ')
        splittedName.forEach((name, index) => {
          if (name.length <= 4) {
            if (splittedName[index + 1] && splittedName[index + 1].length <= 24) {
              splittedName[index] = name + ' ' + splittedName[index + 1]
              splittedName.splice(index + 1, 1)
            }
          }
        })
        return splittedName
      })
      return this.isPdfContent ? units.slice(0, 10) : units
    },
    options() {
      const currentTab = this.tab
      const offerType = this.offerType
      const formatFloat = this.formatFloat
      return {
        chart: {
          toolbar: {
            show: false,
          },
        },
        stroke: {
          show: true,
          colors: 'transparent',
          width: 2,
        },
        xaxis: {
          categories: this.formattedUnits,
          labels: {
            rotate: 0,
            hideOverlappingLabels: false,
          },
          crosshairs: {
            show: false,
            width: 1,
          },
        },
        yaxis: {
          labels: {
            formatter: function (value) {
              return value ? value.toFixed(0) : 0
            },
          },
        },
        legend: {
          horizontalAlign: 'left',
          position: 'bottom',
        },
        plotOptions: {
          bar: {
            columnWidth: this.isPdfContent ? '60%' : '90%',
            borderRadius: 0,
            dataLabels: {
              position: 'top',
            },
          },
        },
        dataLabels: {
          background: {
            enabled: true,
            foreColor: '#555',
          },
          offsetY: -20,
          formatter: function (val, opts) {
            val = formatFloat(val)
            if (offerType === 'combo') {
              if (currentTab === 2 && opts.seriesIndex) val = 'R$' + val
            } else {
              if (currentTab && opts.seriesIndex && offerType !== 'points') val = 'R$' + val
            }
            return val
          },
        },
        colors: ['#002040', '#0079F2', '#00B395'],
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            show: true,
            formatter: function (val, { series, seriesIndex, dataPointIndex, w }) {
              if (currentTab && seriesIndex && offerType !== 'points') val = 'R$' + localeString(val)
              return localeString(val)
            },
          },
        },
      }
    },
    apexChartWidth() {
      return this.options.xaxis.categories.length * 110 + 'px'
    },
  },
  mounted() {
    if (this.isPdfContent) {
      this.tab = this.tabValue
      this.pdfSeries = this.series
    }
  },
  methods: {
    formatFloat(value) {
      if (Number.isInteger(value)) return localeString(value, 0)
      return localeString(value, 2)
    },
    getDataToExport() {
      const concatValue = this.offerType !== 'points' ? '' : 'R$'
      const headers = ['Loja']
      let rows = []
      if (this.offerType === 'combo') {
        headers.push(
          'Unidades vendidas produto alvo (clube e não clube)',
          'Unidades vendidas produto gatilho (clube e não clube)',
          'Unidades vendidas produto alvo (somente clube)',
          'Unidades vendidas produto gatilho (somente clube)',
          'Unidades vendidas participantes da oferta (alvo)',
          'Unidades vendidas participantes da oferta (gatilho)'
        )
        rows = this.metrics.map((unit) => {
          const unitName = unit.unit_name
          const quantityTargetProduct = unit.metrics[this.offerType].products_metrics.totals.total.quantity
          const quantityTriggerProduct = unit.metrics[this.offerType].products_trigger_metrics.totals.total.quantity
          const clubQuantityTargetProduct = unit.metrics[this.offerType].products_metrics.totals.total.loyalty_quantity
          const clubQuantityTriggerProduct = unit.metrics[this.offerType].products_trigger_metrics.totals.total.loyalty_quantity
          const quantityOnOfferTargetProduct = unit.metrics[this.offerType].products_metrics.totals.sales.quantity
          const quantityOnOfferTriggerProduct = unit.metrics[this.offerType].products_trigger_metrics.totals.sales.quantity
          const clients = unit.metrics[this.offerType].products_metrics.totals.sales.clients
          const discounts = this.benefitTypes.includes(this.offerType)
            ? concatValue + this.formatFloat(unit.metrics[this.offerType].products_metrics.totals.sales.cashback)
            : concatValue + this.formatFloat(unit.metrics[this.offerType].products_metrics.totals.sales.discounts)
          return [
            unitName,
            quantityTargetProduct,
            quantityTriggerProduct,
            clubQuantityTargetProduct,
            clubQuantityTriggerProduct,
            quantityOnOfferTargetProduct,
            quantityOnOfferTriggerProduct,
            clients,
            discounts,
          ]
        })
      } else if (this.offerType === 'takepay') {
        headers.push('Unidades vendidas (Clube e não clube)', 'Unidades elegíveis', 'Unidades gratuitas')
        rows = this.metrics.map((unit) => {
          const unitName = unit.unit_name
          const quantityTotal = unit.metrics[this.offerType].products_metrics.totals.total.quantity
          const eligible = unit.metrics[this.offerType].eligible_metrics.quantity
          const quantitySales = unit.metrics[this.offerType].products_metrics.totals.sales.quantity
          const clients = unit.metrics[this.offerType].products_metrics.totals.sales.clients
          const discounts = this.benefitTypes.includes(this.offerType)
            ? concatValue + unit.metrics[this.offerType].products_metrics.totals.sales.cashback
            : concatValue + unit.metrics[this.offerType].products_metrics.totals.sales.discounts
          return [unitName, quantityTotal, eligible, quantitySales, clients, discounts]
        })
      } else {
        headers.push('Unidades vendidas (Clube e não clube)', 'Unidades vendidas (somente clube)', 'Unidades com desconto')

        rows = this.metrics.map((unit) => {
          const unitName = unit.unit_name
          const quantityTotal = unit.metrics[this.offerType].products_metrics.totals.total.quantity
          const loyalty = unit.metrics[this.offerType].products_metrics.totals.total.loyalty_quantity
          const quantitySalesDiscounts = unit.metrics[this.offerType].products_metrics.totals.sales.quantity
          const clients = unit.metrics[this.offerType].products_metrics.totals.sales.clients
          const discounts = this.benefitTypes.includes(this.offerType)
            ? concatValue + unit.metrics[this.offerType].products_metrics.totals.sales.cashback
            : concatValue + unit.metrics[this.offerType].products_metrics.totals.sales.discounts
          return [unitName, quantityTotal, loyalty, quantitySalesDiscounts, clients, discounts]
        })
      }
      headers.push('Clientes', this.benefitTypes.includes(this.offerType) ? 'Benefícios' : 'Descontos')
      return { headers, rows }
    },
  },
}
</script>

<style lang="scss" scoped>
.apex-chart-section {
  overflow-x: auto;
  overflow-y: hidden;
}
.pdf-chart-title {
  color: $marca-1;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
}
</style>
